import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { venueAliasesService } from "@src/pages/VenueAliases/venueAliasesService";
import { Marketplace, MarketplaceVenueSearchResult, VenueAliasGroup } from "@src/pages/VenueAliases/types";

interface VenueAliasesState {
	groups: Array<VenueAliasGroup>;
	total: number;
	currentPage: number;
	itemsPerPage: number;
	searchQuery: string;
	listLoading: "idle" | "pending" | "succeeded" | "failed";
	searchLoading: "idle" | "pending" | "succeeded" | "failed";
	mutationLoading: "idle" | "pending" | "succeeded" | "failed";
	searchResults: Array<MarketplaceVenueSearchResult>;
	searchResultsVenueId: string | null;
	error: string | null;
}

const initialState: VenueAliasesState = {
	groups: [],
	total: 0,
	currentPage: 1,
	itemsPerPage: 30,
	searchQuery: "",
	listLoading: "idle",
	searchLoading: "idle",
	mutationLoading: "idle",
	searchResults: [],
	searchResultsVenueId: null,
	error: null,
};

export const fetchVenueAliasGroups = createAsyncThunk(
	"venueAliases/fetchVenueAliasGroups",
	async (
		{
			page,
			pageSize,
			searchTerm,
			sortBy,
			sortDirection,
		}: {
			page: number;
			pageSize: number;
			searchTerm?: string;
			sortBy?: "sfVenueName" | "sfVenueCity" | "sfVenueState";
			sortDirection?: "asc" | "desc";
		},
		{ rejectWithValue },
	) => {
		try {
			return await venueAliasesService.listVenueAliasGroups({ page, pageSize, searchTerm, sortBy, sortDirection });
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to load venue aliases.");
		}
	},
);

export const deleteVenueAliases = createAsyncThunk(
	"venueAliases/deleteVenueAliases",
	async (
		{
			venueAliasIds,
		}: {
			venueAliasIds: Array<number | string>;
		},
		{ rejectWithValue },
	) => {
		try {
			await venueAliasesService.deleteVenueAliases(venueAliasIds);
			toast.success(venueAliasIds.length === 1 ? "Alias deleted" : "Aliases deleted");
			return venueAliasIds;
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to delete aliases.");
		}
	},
);

// Placeholder for phase one (view + delete). Keeps AddAliasModal compile-safe until BE add/search APIs are available.
export const searchVenueAliasCandidates = createAsyncThunk(
	"venueAliases/searchVenueAliasCandidates",
	async (
		{
			venueId,
		}: {
			venueId: number | string;
			marketplace: Marketplace;
			query: string;
		},
		{ rejectWithValue },
	) => {
		try {
			return { venueId: String(venueId), results: [] as Array<MarketplaceVenueSearchResult> };
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to search marketplace venues.");
		}
	},
);

// Placeholder for phase one (view + delete). Real add API will replace this.
export const addVenueAlias = createAsyncThunk(
	"venueAliases/addVenueAlias",
	async (
		_: {
			venueId: number | string;
			result: MarketplaceVenueSearchResult;
		},
		{ rejectWithValue },
	) => {
		return rejectWithValue("Add alias is not available in this phase.");
	},
);

export const venueAliasesSlice = createSlice({
	name: "venueAliases",
	initialState,
	reducers: {
		setCurrentPage(state, action: PayloadAction<number>) {
			state.currentPage = action.payload;
		},
		setItemsPerPage(state, action: PayloadAction<number>) {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
		setSearchQuery(state, action: PayloadAction<string>) {
			state.searchQuery = action.payload;
			state.currentPage = 1;
		},
		clearSearchResults(state) {
			state.searchResults = [];
			state.searchResultsVenueId = null;
			state.searchLoading = "idle";
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchVenueAliasGroups.pending, (state) => {
				state.listLoading = "pending";
				state.error = null;
			})
			.addCase(fetchVenueAliasGroups.fulfilled, (state, action) => {
				state.listLoading = "succeeded";
				state.groups = action.payload.items;
				state.total = action.payload.total;
			})
			.addCase(fetchVenueAliasGroups.rejected, (state, action) => {
				state.listLoading = "failed";
				state.error = action.payload as string;
			})
			.addCase(searchVenueAliasCandidates.pending, (state, action) => {
				state.searchLoading = "pending";
				state.error = null;
				state.searchResultsVenueId = String(action.meta.arg.venueId);
				state.searchResults = [];
			})
			.addCase(searchVenueAliasCandidates.fulfilled, (state, action) => {
				state.searchLoading = "succeeded";
				state.searchResultsVenueId = action.payload.venueId;
				state.searchResults = action.payload.results;
			})
			.addCase(searchVenueAliasCandidates.rejected, (state, action) => {
				state.searchLoading = "failed";
				state.error = action.payload as string;
			})
			.addCase(addVenueAlias.pending, (state) => {
				state.mutationLoading = "pending";
				state.error = null;
			})
			.addCase(addVenueAlias.fulfilled, (state) => {
				state.mutationLoading = "succeeded";
			})
			.addCase(addVenueAlias.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
			})
			.addCase(deleteVenueAliases.pending, (state) => {
				state.mutationLoading = "pending";
				state.error = null;
			})
			.addCase(deleteVenueAliases.fulfilled, (state, action) => {
				state.mutationLoading = "succeeded";
				const deletedIdSet = new Set(action.payload.map((id) => String(id)));
				state.groups = state.groups.map((group) => ({
					...group,
					aliases: group.aliases.filter((alias) => !deletedIdSet.has(String(alias.id))),
				}));
			})
			.addCase(deleteVenueAliases.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
			});
	},
});

export const { setCurrentPage, setItemsPerPage, setSearchQuery, clearSearchResults } = venueAliasesSlice.actions;

export default venueAliasesSlice.reducer;
